import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { TableLoader } from '../components/TableLoader';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/Modal';

interface EmailHistoryItem {
  id: string;
  certificate_id?: string;
  certificate_number?: string;
  certificate_type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
  company_id?: string;
  company_name?: string;
  recipient_email: string;
  cc_emails?: string;
  subject: string;
  body_html?: string;
  status: 'SENT' | 'FAILED';
  error_message?: string;
  trigger_type: 'AUTO' | 'MANUAL';
  sent_by_user?: string;
  created_at: string;
}

interface EmailStats {
  total: number;
  sent_count: number;
  failed_count: number;
  auto_count: number;
  manual_count: number;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

export function EmailHistoryPage() {
  const [items, setItems] = useState<EmailHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'SENT' | 'FAILED'>('ALL');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING'>('ALL');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [stats, setStats] = useState<EmailStats>({
    total: 0,
    sent_count: 0,
    failed_count: 0,
    auto_count: 0,
    manual_count: 0,
  });

  // Selected item for detail view modal
  const [selectedItem, setSelectedItem] = useState<EmailHistoryItem | null>(null);

  const loadHistory = () => {
    setLoading(true);
    setError('');
    api.renewalEmails
      .getHistory({
        page,
        limit: 20,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        type: typeFilter === 'ALL' ? undefined : typeFilter,
        search: search.trim() || undefined,
      })
      .then((res: any) => {
        setItems(res.data || []);
        if (res.pagination) {
          setTotalPages(res.pagination.total_pages || 1);
          setTotalCount(res.pagination.total || 0);
        }
        if (res.stats) {
          setStats(res.stats);
        } else if (res.pagination) {
          setStats((prev) => ({
            ...prev,
            total: res.pagination.total || 0,
          }));
        }
      })
      .catch((err: ApiError) => {
        setError(err.message || 'Failed to load email history');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadHistory();
  }, [page, statusFilter, typeFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadHistory();
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* ── Top Bar & Description ───────────────── */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">✉️</span>
            <h2 className="text-lg font-bold text-gray-900">Email History &amp; Audit Log</h2>
            <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
              {totalCount} item{totalCount !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 flex items-center gap-1.5">
            <span>⏱️</span>
            <span>Audit trail of all automated cron and manual renewal reminder emails dispatched from the system.</span>
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={loadHistory}
            className="btn btn-secondary text-xs sm:text-sm flex items-center gap-1.5 cursor-pointer"
          >
            <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ── Summary Metric Cards ──────────────────── */}
      <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card p-3.5 border-l-4 border-l-blue-500">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total Dispatched</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 text-slate-900">{stats.total}</p>
        </div>
        <div className="card p-3.5 border-l-4 border-l-emerald-500">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Delivered</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 text-emerald-600">{stats.sent_count}</p>
        </div>
        <div className="card p-3.5 border-l-4 border-l-red-500">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Failed Dispatches</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 text-red-600">{stats.failed_count}</p>
        </div>
        <div className="card p-3.5 border-l-4 border-l-indigo-500">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Automated Cron</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 text-indigo-600">{stats.auto_count}</p>
        </div>
      </div>

      {/* ── Filter Tabs & Search Bar ─────────────── */}
      <div className="shrink-0 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        {/* Filter Pills */}
        <div className="flex items-center flex-wrap gap-2">
          {/* Status Toggle Pills */}
          <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
            {(['ALL', 'SENT', 'FAILED'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                  statusFilter === s
                    ? s === 'FAILED'
                      ? 'bg-white text-red-700 shadow-xs'
                      : 'bg-white text-gray-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {s === 'ALL' ? 'All Status' : s === 'SENT' ? 'Sent (✓)' : 'Failed (✗)'}
              </button>
            ))}
          </div>

          {/* Form Type Toggle Pills */}
          <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
            {(['ALL', 'FORM_9', 'FORM_10', 'FORM_11', 'TRAINING'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setTypeFilter(t); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                  typeFilter === t
                    ? 'bg-white text-gray-900 shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t === 'ALL' ? 'All Types' : t === 'FORM_9' ? 'Form 9' : t === 'FORM_10' ? 'Form 10' : t === 'FORM_11' ? 'Form 11' : 'Training'}
              </button>
            ))}
          </div>
        </div>

        {/* Search Input */}
        <div className="relative w-full lg:w-72 shrink-0">
          <input
            type="text"
            placeholder="Search company, cert, recipient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input text-xs sm:text-sm pl-8 pr-8 w-full"
          />
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🔍</span>
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="shrink-0 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-sm font-semibold cursor-pointer">✕</button>
        </div>
      )}

      {/* ── Main Items Table ──────────────────────── */}
      {loading ? (
        <TableLoader title="Loading Email Audit History..." />
      ) : (
        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto">
            <table className="w-full text-left text-xs sm:text-sm border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10 text-gray-600 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="p-3 sm:px-4">Date &amp; Time</th>
                  <th className="p-3 sm:px-4">Status</th>
                  <th className="p-3 sm:px-4">Trigger</th>
                  <th className="p-3 sm:px-4">Cert No.</th>
                  <th className="p-3 sm:px-4">Type</th>
                  <th className="p-3 sm:px-4">Company</th>
                  <th className="p-3 sm:px-4">Recipient</th>
                  <th className="p-3 sm:px-4">Subject</th>
                  <th className="p-3 sm:px-4 hidden md:table-cell">Sent By</th>
                  <th className="p-3 sm:px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-16 text-gray-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span className="text-4xl">✉️</span>
                        <p className="font-semibold text-gray-700 text-base">No Email Records Found</p>
                        <p className="text-xs text-gray-400 max-w-sm">
                          {search || statusFilter !== 'ALL' || typeFilter !== 'ALL'
                            ? 'No email records matched your current filters.'
                            : 'Automated renewal reminders and manually sent notices will appear here.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-gray-50/80 transition-colors ${
                        item.status === 'FAILED' ? 'bg-red-50/30' : ''
                      }`}
                    >
                      <td className="p-3 sm:px-4 text-xs text-gray-600 whitespace-nowrap font-medium">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="p-3 sm:px-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            item.status === 'SENT'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${item.status === 'SENT' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          <span>{item.status}</span>
                        </span>
                      </td>
                      <td className="p-3 sm:px-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            item.trigger_type === 'AUTO'
                              ? 'bg-sky-50 text-sky-700 border border-sky-200'
                              : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                          }`}
                        >
                          {item.trigger_type === 'AUTO' ? 'Auto Cron' : 'Manual'}
                        </span>
                      </td>
                      <td className="p-3 sm:px-4 font-mono text-xs font-bold text-brand-700 whitespace-nowrap">
                        {item.certificate_number || '—'}
                      </td>
                      <td className="p-3 sm:px-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            item.certificate_type === 'FORM_11'
                              ? 'bg-purple-50 text-purple-700 border border-purple-200'
                              : item.certificate_type === 'TRAINING'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {item.certificate_type === 'FORM_9' ? 'Form 9' : item.certificate_type === 'FORM_11'
                            ? 'Form 11'
                            : item.certificate_type === 'TRAINING'
                            ? 'Training'
                            : 'Form 10'}
                        </span>
                      </td>
                      <td className="p-3 sm:px-4 text-xs font-semibold text-gray-900 max-w-[180px] truncate" title={item.company_name}>
                        {item.company_name || '—'}
                      </td>
                      <td className="p-3 sm:px-4 text-xs max-w-[200px] truncate">
                        <span className="font-mono text-gray-900 block truncate">{item.recipient_email}</span>
                        {item.cc_emails && (
                          <span className="block text-[10px] text-gray-400 font-mono truncate mt-0.5" title={`CC: ${item.cc_emails}`}>
                            CC: {item.cc_emails}
                          </span>
                        )}
                      </td>
                      <td className="p-3 sm:px-4 text-xs text-gray-700 max-w-xs truncate" title={item.subject}>
                        {item.subject}
                      </td>
                      <td className="p-3 sm:px-4 text-xs text-gray-500 whitespace-nowrap hidden md:table-cell">
                        {item.sent_by_user || '—'}
                      </td>
                      <td className="p-3 sm:px-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setSelectedItem(item)}
                          className="btn btn-secondary text-xs py-1 px-2.5 flex items-center gap-1.5 ml-auto cursor-pointer"
                          title="View rendered email content"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="shrink-0 p-3 border-t border-gray-200 bg-gray-50/50">
            <Pagination page={page} totalPages={totalPages} total={totalCount} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* ── View Email Detail Modal ──────────────── */}
      {selectedItem && (
        <Modal
          open={Boolean(selectedItem)}
          onClose={() => setSelectedItem(null)}
          title={`Email Dispatch: ${selectedItem.subject}`}
          size="xl"
        >
          <div className="flex flex-col max-h-[82vh] space-y-4">
            {/* Meta summary card */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 text-xs space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-bold text-xs ${
                      selectedItem.status === 'SENT'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <span>{selectedItem.status === 'SENT' ? '✓ SENT' : '✗ FAILED'}</span>
                  </span>
                  <span className="text-slate-600">
                    Dispatched on <strong>{formatDateTime(selectedItem.created_at)}</strong> via{' '}
                    <strong>{selectedItem.trigger_type === 'AUTO' ? 'Automated Daily Cron' : 'Manual Trigger'}</strong>
                  </span>
                </div>
                <span className="text-slate-500">
                  User: <strong>{selectedItem.sent_by_user || 'SYSTEM'}</strong>
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                <div>
                  <span className="text-slate-500">Company:</span>{' '}
                  <strong className="text-slate-800">{selectedItem.company_name || '—'}</strong>
                </div>
                <div>
                  <span className="text-slate-500">Certificate:</span>{' '}
                  <strong className="font-mono text-slate-800">
                    {selectedItem.certificate_number || '—'} ({selectedItem.certificate_type || '—'})
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500">To:</span>{' '}
                  <strong className="font-mono text-slate-800">{selectedItem.recipient_email}</strong>
                </div>
                {selectedItem.cc_emails && (
                  <div>
                    <span className="text-slate-500">CC:</span>{' '}
                    <strong className="font-mono text-slate-800">{selectedItem.cc_emails}</strong>
                  </div>
                )}
              </div>

              {selectedItem.error_message && (
                <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <strong>Error details:</strong> {selectedItem.error_message}
                </div>
              )}
            </div>

            {/* Rendered Email Body in Iframe */}
            <div className="flex-1 min-h-[380px] border border-slate-200 rounded-xl overflow-hidden bg-slate-50 p-2">
              <iframe
                title="Sent Email Content"
                srcDoc={selectedItem.body_html || '<p style="padding: 20px; font-family: sans-serif;">No HTML body recorded</p>'}
                className="w-full h-full min-h-[380px] bg-white rounded-lg border border-slate-200 shadow-inner"
              />
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="btn btn-secondary text-xs py-2 px-4 cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
